import { ChangeDetectorRef, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import { Chip } from 'primeng/chip';
import { Skeleton } from 'primeng/skeleton';
import { PageComponent } from '../../../infrastructure/shared/components/page/page';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import {
  CompanyPackageStatsForAdminResponse,
  EnumDtoOfUserPackageStatus,
  GetPackagesForCompanyQuery,
  IPaginationResponseOfPackageForCompanyTableResponse,
  PackageFilter,
  PackageForCompanyTableResponse,
  PackagesClient,
  UserPackageStatus,
} from '../../../infrastructure/shared/model/fss.api';
import { UserService } from '../../../infrastructure/shared/services/user.service';
import { Cube01 } from '../../../infrastructure/shared/icons/cube-01/cube-01';
import { ClockFastForward } from '../../../infrastructure/shared/icons/clock-fast-forward/clock-fast-forward';
import { Loading01 } from '../../../infrastructure/shared/icons/loading-01/loading-01';
import { CheckCircle } from '../../../infrastructure/shared/icons/check-circle/check-circle';
import { ClockRewind } from '../../../infrastructure/shared/icons/clock-rewind/clock-rewind';
import { Hourglas03 } from '../../../infrastructure/shared/icons/hourglas-03/hourglas-03';
import { XClose } from '../../../infrastructure/shared/icons/x-close/x-close';
import { DataTable } from '../../../infrastructure/shared/components/data-table/data-table';
import { InfoCircle } from '../../../infrastructure/shared/icons/info-circle/info-circle';
import {
  getDateFormatedSr,
  getDateFormatedSrWithTime,
} from '../../../infrastructure/shared/services/date.service';

type SubTab = 0 | 1 | 2;

@Component({
  standalone: true,
  selector: 'app-company-packages-dashboard',
  imports: [
    PageComponent,
    ButtonComponent,
    Tab,
    TabList,
    Tabs,
    Chip,
    Skeleton,
    Cube01,
    ClockFastForward,
    Loading01,
    CheckCircle,
    ClockRewind,
    Hourglas03,
    XClose,
    InfoCircle,
    DataTable,
  ],
  templateUrl: './company-packages-dashboard.html',
  styleUrl: './company-packages-dashboard.scss',
})
export class CompanyPackagesDashboard {
  private _client = inject(PackagesClient);
  private _router = inject(Router);
  private _user = inject(UserService);
  private _cdr = inject(ChangeDetectorRef);

  protected packages: PackageForCompanyTableResponse[] = [];
  protected first = 0;
  protected isLoading = true;
  protected activeTab = signal<SubTab>(0);

  protected paginatedData: IPaginationResponseOfPackageForCompanyTableResponse = {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  } as IPaginationResponseOfPackageForCompanyTableResponse;

  protected stats = signal<CompanyPackageStatsForAdminResponse | undefined>(undefined);
  protected pendingCount = signal<number>(0);
  protected statsLoading = signal<boolean>(true);

  protected onboardingDismissed = signal<boolean>(false);

  protected showOnboardingBanner = computed(() => {
    if (this.onboardingDismissed()) return false;
    if (this.statsLoading()) return false;
    const total = this.stats()?.totalHours ?? 0;
    return total === 0 && this.pendingCount() === 0;
  });

  protected showProcessingBanner = computed(() => this.pendingCount() > 0);
  protected hasNoPackages = computed(() => (this.stats()?.totalHours ?? 0) === 0);
  protected showHeaderAction = computed(() => !this.showOnboardingBanner());

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
  protected readonly UserPackageStatus = UserPackageStatus;
  protected readonly getDateFormatedSr = getDateFormatedSr;
  protected readonly getDateFormatedSrWithTime = getDateFormatedSrWithTime;

  constructor() {
    this._loadStats();
    this._loadPendingCount();
  }

  protected dismissOnboarding(): void {
    this.onboardingDismissed.set(true);
  }

  protected goToOffer(): void {
    this._router.navigate(['/company/packages/offer']);
  }

  protected onTabChange(value: number | string | undefined): void {
    this.activeTab.set((Number(value ?? 0) as SubTab) ?? 0);
    this.paginatedData.currentPage = 1;
    this._loadTable();
  }

  protected loadLazy(event: any): void {
    const first = event?.first ?? 0;
    const rows = event?.rows ?? this.paginatedData.pageSize ?? 10;
    this.paginatedData.pageSize = rows;
    this.paginatedData.currentPage = Math.floor(first / rows) + 1;
    this._loadTable();
  }

  protected prev(): void {
    this.paginatedData.currentPage!--;
    this._loadTable();
  }

  protected next(): void {
    this.paginatedData.currentPage!++;
    this._loadTable();
  }

  protected getAvailableHours(p: PackageForCompanyTableResponse): number {
    return p.progress?.availableHours ?? 0;
  }

  protected getTotalHours(p: PackageForCompanyTableResponse): number {
    return p.progress?.totalHours ?? 0;
  }

  protected getBarPercent(p: PackageForCompanyTableResponse): number {
    const total = this.getTotalHours(p);
    if (!total) return 0;
    return Math.round((this.getAvailableHours(p) / total) * 100);
  }

  protected isBarRed(p: PackageForCompanyTableResponse): boolean {
    const total = this.getTotalHours(p);
    if (!total) return false;
    return this.getAvailableHours(p) / total <= 0.2;
  }

  protected getStatusChipClass(status: EnumDtoOfUserPackageStatus | undefined): string {
    switch (status?.value) {
      case UserPackageStatus.NEW:
        return 'info sm';
      case UserPackageStatus.INVOICE_SENT:
        return 'warn sm';
      case UserPackageStatus.ACTIVE:
      case UserPackageStatus.EXTENDED:
        return 'success sm';
      case UserPackageStatus.CANCELLED:
        return 'red sm';
      case UserPackageStatus.EXPIRED:
        return 'gray sm';
      default:
        return 'gray sm';
    }
  }

  protected getIstorijaChipLabel(p: PackageForCompanyTableResponse): string {
    if (p.status?.value === UserPackageStatus.CANCELLED) {
      return p.status?.description ?? '';
    }
    if (this.getAvailableHours(p) === 0) return 'Iskorišćen';
    if (p.validTo && new Date(p.validTo).getTime() < Date.now()) return 'Istekao';
    return p.status?.description ?? '';
  }

  protected getIstorijaChipClass(p: PackageForCompanyTableResponse): string {
    if (p.status?.value === UserPackageStatus.CANCELLED) return 'red sm';
    if (this.getAvailableHours(p) === 0) return 'gray sm';
    if (p.validTo && new Date(p.validTo).getTime() < Date.now()) return 'red sm';
    return 'gray sm';
  }

  protected getNextStepLabel(p: PackageForCompanyTableResponse): string {
    if (p.nextStep) return p.nextStep;
    switch (p.status?.value) {
      case UserPackageStatus.NEW:
        return 'Faktura u pripremi';
      case UserPackageStatus.INVOICE_SENT:
        return 'Čeka se uplata';
      default:
        return '-';
    }
  }

  private async _loadStats(): Promise<void> {
    this.statsLoading.set(true);
    const userId = this._user.getUserData.id;
    const res = await firstValueFrom(this._client.getCompanyPackageStatsForAdmin(userId));
    this.stats.set(res.result);
    this.statsLoading.set(false);
  }

  private async _loadPendingCount(): Promise<void> {
    const res = await firstValueFrom(
      this._client.getPackagesForCompany({
        filter: {
          statuses: [UserPackageStatus.NEW, UserPackageStatus.INVOICE_SENT],
        } as PackageFilter,
        pageNumber: 1,
        pageSize: 1,
      } as GetPackagesForCompanyQuery),
    );
    this.pendingCount.set(res.result.totalCount ?? 0);
  }

  private _loadTable(): void {
    this.isLoading = true;
    this.packages = [];
    this._cdr.detectChanges();

    this._client
      .getPackagesForCompany({
        filter: { statuses: this._statusesForTab() } as PackageFilter,
        pageNumber: this.paginatedData.currentPage,
        pageSize: this.paginatedData.pageSize,
      } as GetPackagesForCompanyQuery)
      .subscribe((res) => {
        this.packages = res.result.data ?? [];
        this.paginatedData = res.result;
        this.first =
          (this.paginatedData.currentPage! - 1) * this.paginatedData.pageSize!;
        this.isLoading = false;
        this._cdr.markForCheck();
      });
  }

  private _statusesForTab(): UserPackageStatus[] {
    switch (this.activeTab()) {
      case 0:
        return [UserPackageStatus.ACTIVE, UserPackageStatus.EXTENDED];
      case 1:
        return [UserPackageStatus.CANCELLED, UserPackageStatus.EXPIRED];
      case 2:
        return [UserPackageStatus.NEW, UserPackageStatus.INVOICE_SENT];
      default:
        return [];
    }
  }
}
